// The label store of a plain-DOM module bundle (pt-BR pack, ruling R-21).
//
// A module under studio/frontend/* is an IIFE the page loads on its own; it never shares the
// components bundle's label store (`setLabels` / `t` of studio/src-js/components/src/utils/labels.ts)
// and never imports from the components tree. Its strings are catalog keys of ONE `_module` section
// (a section whose name ends in `_module`): the English SOURCE lives in the module's own `labels.ts`
// (a map checked against the PHP `ModuleLabels` class by studio/tooling/i18n/module-labels.test.mjs),
// and the Portuguese reaches the module through its own bootstrap channel, read once at boot.
//
// `t(key, vars)` has the same semantics as the components `t()`: `%name` placeholders are replaced
// in ONE pass over the original template (see `interpolate` below). A missing or malformed feed never throws and never blocks the mount: the
// module keeps rendering its English source.
export type ModuleLabels = {
  /** The section this store serves (`photo_library_module`). */
  readonly section: string;
  /** The English source map, keyed by full dotted key. */
  readonly source: Readonly<Record<string, string>>;
  /** Replace the runtime layer from a feed (`{ bare_key: "text" }`, or `{ section: { bare_key: "text" } }`). */
  setFeed(feed: unknown): void;
  /** Resolve a full dotted key: runtime layer, then the English source, then the key itself. */
  t(key: string, vars?: Record<string, string | number>): string;
  /**
   * Plural-aware `t()`. The plural rule rides the catalog like the `.po` `Plural-Forms` header: the key
   * `<section>.plural_rule` (English `n != 1`, pt-BR `n > 1`). Only those two exact strings are understood
   * (no expression is ever evaluated); any other value falls back to `n != 1`. `count` is added to the vars
   * unless the caller passed one.
   */
  tn(oneKey: string, otherKey: string, count: number, vars?: Record<string, string | number>): string;
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
  value !== null && typeof value === 'object' && !Array.isArray(value);

// Single-pass `%name` interpolation (Gate 2 fix wave 1, R1-01): placeholders are matched in the ORIGINAL template
// only, so text inserted for one variable (a customer filename holding "%mb") is never re-scanned by another. The
// match is prefix-greedy BY DESIGN (no word boundary after the name): shipped vendor templates glue a unit to the
// token ("max %maxMB") and must render "20MB". The longest supplied name wins at a position, so a glued token renders
// the value followed by the suffix, and a `%word` is left as it is only when no supplied name is a prefix of it.
// Values go in literally (a replacer function, never a replacement pattern). Same function as the components store.
function interpolate(label: string, vars?: Record<string, string | number>): string {
  const names = vars ? Object.keys(vars).filter((name) => name !== '') : [];
  if (!vars || names.length === 0) return label;
  const alternatives = names
    .sort((a, b) => b.length - a.length)
    .map((name) => name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'))
    .join('|');
  return label.replace(new RegExp(`%(${alternatives})`, 'g'), (_match, name: string) => String(vars[name]));
}

export function createModuleLabels(section: string, source: Readonly<Record<string, string>>): ModuleLabels {
  let runtime: Record<string, string> = {};
  const prefix = `${section}.`;
  const resolve = (key: string, vars?: Record<string, string | number>): string => {
    return interpolate(runtime[key] ?? source[key] ?? key, vars);
  };
  return {
    section,
    source,
    setFeed(feed: unknown): void {
      // A feed is the layered map of this module's section: bare key -> non-empty string. The
      // whole-catalog shape (`{ section: { ... } }`) is accepted too so a host can pass its object
      // through unchanged. Anything else leaves the English source in place.
      const entries = isRecord(feed) && isRecord(feed[section]) ? (feed[section] as Record<string, unknown>) : feed;
      const next: Record<string, string> = {};
      if (isRecord(entries)) {
        for (const [bare, value] of Object.entries(entries)) {
          if (typeof value === 'string' && value !== '') next[`${prefix}${bare}`] = value;
        }
      }
      runtime = next;
    },
    t: resolve,
    tn(oneKey: string, otherKey: string, count: number, vars?: Record<string, string | number>): string {
      const rule = resolve(`${prefix}plural_rule`).trim();
      const singular = rule === 'n > 1' ? count <= 1 : count === 1;
      return resolve(singular ? oneKey : otherKey, vars && 'count' in vars ? vars : { ...vars, count });
    },
  };
}

/**
 * Parse the JSON a module's channel carries in an attribute (`data-labels`, `module-labels`). An absent,
 * empty or malformed attribute is `undefined`: the module keeps its English source and the mount goes on.
 */
export function parseLabelsFeed(raw: string | null | undefined): unknown {
  if (typeof raw !== 'string' || raw === '') return undefined;
  try {
    const parsed: unknown = JSON.parse(raw);
    return isRecord(parsed) ? parsed : undefined;
  } catch {
    return undefined;
  }
}
